import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faCalendarDays } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";

const inputCls =
  "w-full px-3 mt-1 text-sm py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500";
const labelCls = "block text-xs font-medium text-gray-600";

// Local-datetime-input helper — <input type="datetime-local"> wants
// "YYYY-MM-DDTHH:mm" in LOCAL time, with no timezone suffix.
const toLocalInput = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const AddEvent = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    slug: "", publicLabel: "", name: "",
    startAtLocal: "", durationMin: 120,
    city: "", community: "", professionTag: "",
    maritalPool: "never_married",
    basePrice: 299, capacityPresenters: 24, maxGenderRatio: 1.3,
    meetingUrl: "",
    meetingPasscode: "",
    womenFree: true, hostPhone: "", status: "draft",
  });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    const slug = form.slug.toLowerCase().trim();
    if (!slug) return toast.warn("Slug is required");
    if (!/^[a-z0-9-]+$/.test(slug)) return toast.warn("Slug may only contain lowercase letters, numbers and hyphens");
    if (!form.publicLabel.trim()) return toast.warn("Public label is required");
    if (!form.startAtLocal) return toast.warn("Start date/time is required");

    setSaving(true);
    try {
      const payload = {
        slug,
        publicLabel: form.publicLabel.trim(),
        name: form.name.trim() || form.publicLabel.trim(),
        startAt: new Date(form.startAtLocal).toISOString(),
        durationMin: Number(form.durationMin) || 120,
        city: form.city.trim() || null,
        community: form.community.trim() || null,
        professionTag: form.professionTag.trim() || null,
        maritalPool: form.maritalPool,
        basePrice: Number(form.basePrice) || 0,
        capacityPresenters: Number(form.capacityPresenters) || 24,
        maxGenderRatio: Number(form.maxGenderRatio) || 1.3,
        meetingUrl: form.meetingUrl.trim() || null,
        meetingPasscode: form.meetingPasscode.trim() || null,
        womenFree: !!form.womenFree,
        status: form.status,
      };
      if (form.hostPhone.trim()) payload.hostPhone = form.hostPhone.trim();

      const res = await func.post(urls.CREATE_EVENT_URL, payload);
      if (res?.status === 201 || res?.status === 200) {
        toast.success("Event created");
        navigate("/events/list");
      } else {
        toast.warn(res?.data?.message || "Failed to create event");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create event");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition text-white"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-sm" />
          </button>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faCalendarDays} className="text-white" />
            <h1 className="text-xl font-bold text-white">Add Event</h1>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 space-y-6">
          <section className="space-y-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Identity</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Slug (matchmatrix.in/e/&lt;slug&gt;) *</label>
                <input className={inputCls} value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase())} placeholder="sunday-meet" />
              </div>
              <div>
                <label className={labelCls}>Internal name (defaults to public label)</label>
                <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Optional" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Public label *</label>
              <input className={inputCls} value={form.publicLabel} onChange={(e) => set("publicLabel", e.target.value)} placeholder="Community Match Meet" />
            </div>
          </section>

          <section className="space-y-4 pt-2 border-t border-gray-100">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Schedule</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Start (your local time) *</label>
                <input type="datetime-local" className={inputCls} value={form.startAtLocal} onChange={(e) => set("startAtLocal", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Duration (minutes)</label>
                <input type="number" className={inputCls} value={form.durationMin} onChange={(e) => set("durationMin", e.target.value)} />
              </div>
            </div>
          </section>

          <section className="space-y-4 pt-2 border-t border-gray-100">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Audience (targeting walls)</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>City</label>
                <input className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Bengaluru" />
              </div>
              <div>
                <label className={labelCls}>Community</label>
                <input className={inputCls} value={form.community} onChange={(e) => set("community", e.target.value)} placeholder="Vokkaliga" />
              </div>
              <div>
                <label className={labelCls}>Profession tag</label>
                <input className={inputCls} value={form.professionTag} onChange={(e) => set("professionTag", e.target.value)} placeholder="doctors" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Marital pool</label>
              <select className={inputCls} value={form.maritalPool} onChange={(e) => set("maritalPool", e.target.value)}>
                <option value="never_married">Never married</option>
                <option value="second_marriage">Second marriage</option>
              </select>
            </div>
          </section>

          <section className="space-y-4 pt-2 border-t border-gray-100">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Capacity &amp; pricing</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Capacity (presenters)</label>
                <input type="number" className={inputCls} value={form.capacityPresenters} onChange={(e) => set("capacityPresenters", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Max gender ratio</label>
                <input type="number" step="0.1" className={inputCls} value={form.maxGenderRatio} onChange={(e) => set("maxGenderRatio", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Base price (₹)</label>
                <input type="number" className={inputCls} value={form.basePrice} onChange={(e) => set("basePrice", e.target.value)} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.womenFree} onChange={(e) => set("womenFree", e.target.checked)} className="rounded border-gray-300" />
              Women free
            </label>
          </section>

          <section className="space-y-4 pt-2 border-t border-gray-100">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Host &amp; status</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Host phone number</label>
                <input className={inputCls} value={form.hostPhone} onChange={(e) => set("hostPhone", e.target.value)} placeholder="9XXXXXXXXX" />
                <p className="text-xs text-gray-400 mt-1">The host must already have logged into the app once with this number. Leave blank to set later.</p>
              </div>

            <div className="col-span-2">
              <label className={labelCls}>Joining link (Zoom / Meet)</label>
              <input
                className={inputCls}
                value={form.meetingUrl}
                onChange={(e) => set("meetingUrl", e.target.value)}
                placeholder="https://zoom.us/j/… — where the guests actually see each other"
              />
              <p className="text-xs text-gray-400 mt-1">
                The in-page video embed is not configured, so this link is the only
                video path for the room. Without it, guests get a score screen and
                nowhere to meet.
              </p>
            </div>
            <div>
              <label className={labelCls}>Meeting passcode</label>
              <input
                className={inputCls}
                value={form.meetingPasscode}
                onChange={(e) => set("meetingPasscode", e.target.value)}
                placeholder="Zoom usually needs one"
              />
            </div>

              <div>
                <label className={labelCls}>Status</label>
                <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                  <option value="draft">Draft</option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="approved">Approved</option>
                  <option value="live">Live</option>
                  <option value="closed">Closed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">Draft is not publicly visible. Approved (or live) is required before anyone can register.</p>
              </div>
            </div>
          </section>

          <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
            <button onClick={() => navigate(-1)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium disabled:opacity-50"
            >
              {saving ? "Saving…" : "Create Event"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEvent;
